import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@noor/lib/cn';

/**
 * `SegmentedNav` — a pill-shaped segmented control for primary, top-level
 * navigation between the sections of a super-app "part". A filled track holds a
 * row of segments; the active segment lifts onto a raised card surface with
 * brand-tinted text, matching the Figma "Segmented picker".
 *
 * Presentational only: the caller decides which segment is `active` and renders
 * each as a `<Link>` via `asChild` (keeps the control router-agnostic).
 *
 * ```tsx
 * <SegmentedNav>
 *   <SegmentedNav.Item asChild active>
 *     <Link to="/">Dashboard</Link>
 *   </SegmentedNav.Item>
 *   <SegmentedNav.Item asChild>
 *     <Link to="/orders">Orders</Link>
 *   </SegmentedNav.Item>
 * </SegmentedNav>
 * ```
 */
function SegmentedNav({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="segmented-nav"
      role="tablist"
      className={cn('flex items-center gap-2 rounded-full bg-muted p-1', className)}
      {...props}
    />
  );
}

const segmentedNavItemVariants = cva(
  [
    "inline-flex items-center justify-center whitespace-nowrap rounded-full px-6 py-4 text-sm tracking-tight outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
    // Color/weight cross-fade on hover + active, plus a subtle press affordance.
    // The raised active card additionally slides via a shared-element view
    // transition (see `.nav-segment-pill` below) when navigation animates.
    'transition-[color,background-color,box-shadow,transform] duration-200 ease-out active:scale-[0.97]',
  ],
  {
    variants: {
      active: {
        true: 'nav-segment-pill bg-card font-semibold text-primary shadow-xs',
        false:
          'font-medium text-secondary-foreground hover:text-foreground hover:bg-card/40',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type SegmentedNavItemProps = React.ComponentProps<'button'> &
  VariantProps<typeof segmentedNavItemVariants> & {
    /**
     * Merge props onto the single child element (a router `<Link>`) instead of
     * rendering a `<button>`, keeping the control router-agnostic.
     */
    asChild?: boolean;
  };

function SegmentedNavItem({
  className,
  active = false,
  asChild = false,
  ...props
}: SegmentedNavItemProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="segmented-nav-item"
      data-active={active}
      role="tab"
      aria-selected={active ?? false}
      className={cn(segmentedNavItemVariants({ active }), className)}
      {...props}
    />
  );
}

SegmentedNav.Item = SegmentedNavItem;

export { SegmentedNav, SegmentedNavItem, segmentedNavItemVariants };
